import { Injectable } from '@angular/core';
import { CurrentUser } from '@models/Core/CurrentUser';

import { BusService } from '../bus.service';
import { EventsService } from '../events.service';
import { RequestService } from '../request.service';

export interface ILoginBody {
  email: string;
  passsword: string;
}

export interface ILoginResponse {
  token: string;
}

export interface ICreateUserBody {
  email: string;
  first_name: string;
  name: string;
  password: string;
}

export interface ICreateUserResponse {
  token: string;
}

export interface IChangePasswordBody {
  oldPassword: string;
  newPassword: string;
}

export interface IChangePasswordRequest {
  userId: string;
  token?: string;
  body: IChangePasswordBody;
}

export type IGetCurrentUserResponse = CurrentUser;

@Injectable()
export class AuthenticationRequestService {
  api = {
    login: '/auth/local',
    root: '/users',
    changePassword: '/password',
    forgotPassword: '/forgot-password',
    currentUser: '/me',
  };

  constructor(
    private client: RequestService,
    private bus: BusService,
    private events: EventsService,
  ) {
    this.subscribe();
  }

  login(credentials: ILoginBody) {
    this.client.post<ILoginBody, ILoginResponse>({
      uri: this.api.login,
      body: credentials,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.authentication.login.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.authentication.login.failure,
            error.error,
          ),
      },
    });
  }

  createUser(user: ICreateUserBody) {
    this.client.post<ICreateUserBody, ICreateUserResponse>({
      uri: this.api.root,
      body: user,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.user.create.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.user.create.failure,
            error.error,
          ),
      },
    });
  }

  changePassword(request: IChangePasswordRequest) {
    let uri = this.api.root + '/' + request.userId + this.api.changePassword;
    if (request.token) uri += `?access_token=${request.token}`;
    this.client.put<IChangePasswordBody, any>({
      uri: uri,
      body: request.body,
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.user.changePassword.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.user.changePassword.failure,
            error.error,
          ),
      },
    });
  }

  forgotPassword(email) {
    this.client.post<any, any>({
      uri: this.api.root + this.api.forgotPassword,
      body: {
        email: email,
      },
      handlers: {
        success: response =>
          this.bus.publish(
            this.events.received.action.user.forgotPassword.success,
            response,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.user.forgotPassword.failure,
            error.error,
          ),
      },
    });
  }

  update(user) {
    return this.client.put<any, any>({
      uri: this.api.root + '/' + user._id,
      body: user,
      handlers: {
        success: () =>
          this.bus.publish(
            this.events.received.action.user.update.success,
            user,
          ),
        error: error =>
          this.bus.publish(
            this.events.received.action.user.update.failure,
            error,
          ),
      },
    });
  }

  getCurrentUser() {
    this.client.get<IGetCurrentUserResponse>({
      uri: this.api.root + this.api.currentUser,
      handlers: {
        success: response =>
          this.bus.publish(this.events.received.data.user.me.success, response),
        error: error =>
          this.bus.publish(
            this.events.received.data.user.me.failure,
            error.error,
          ),
      },
    });
  }

  private subscribe() {
    this.bus.subscribe(
      this.events.requested.action.authentication.login,
      this.login.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.user.create,
      this.createUser.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.user.changePassword,
      this.changePassword.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.user.forgotPassword,
      this.forgotPassword.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.data.user.me,
      this.getCurrentUser.bind(this),
    );
    this.bus.subscribe(
      this.events.requested.action.user.update,
      this.update.bind(this),
    );
  }
}
